import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { getSearchAPI } from "@/redux/Purchase/actions";
import { useDispatch } from "react-redux";
import { useDebounced } from "@/hooks/useDebounced";
import { SearchX } from "lucide-react";

export const SearchBarPagination = ({
    selectReacord,
    searchAPI,
    value,
    inputStyle = "",
    className = "",
    placeholder = "Type to search...",
    showResults = 1,
    onchange = () => { },
    renderItem,
    paramCompanyId = "CompanyId",
    /** Used when the field is focused but the user has typed fewer than `minChars` — loads sample rows so the control feels alive. */
    defaultSearch = "a",
    /** Minimum trimmed characters before search uses typed text instead of only `defaultSearch`. */
    minChars = 1,
    /** Dropdown stacking order — use a high value inside modals/drawers (e.g. 10050). */
    dropdownZIndex = 10050,
}) => {
    const [input, setInput] = useState(value || "");
    const [inputSelect, setInputSelect] = useState(value || "");
    const [searchResult, setSearchResult] = useState([]);
    const [searchResultObj, setSearchResultObj] = useState([]);
    const [dropdownPos, setDropdownPos] = useState(null);
    const [isOpen, setIsOpen] = useState(false);

    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);

    const dispatch = useDispatch();
    const wrapperRef = useRef(null);
    const dropdownRef = useRef(null);
    const isOpenRef = useRef(false);
    const [MasterCompanyDetails] = JSON.parse(
        localStorage.getItem("MasterCompanyDetails") ?? "[{}]",
    );
    /** Prefix backing the current list (typed query or `defaultSearch`) — used for infinite scroll paging. */
    const paginationPrefixRef = useRef("");

    useEffect(() => {
        setInput(value || "");
    }, [value]);
    const updatePosition = () => {
        if (!wrapperRef.current) return;

        const rect = wrapperRef.current.getBoundingClientRect();

        setDropdownPos({
            top: rect.bottom + window.scrollY,
            left: rect.left + window.scrollX,
            width: rect.width,
        });
    };

    useEffect(() => {
        window.addEventListener("scroll", updatePosition, true);
        window.addEventListener("resize", updatePosition);

        return () => {
            window.removeEventListener("scroll", updatePosition, true);
            window.removeEventListener("resize", updatePosition);
        };
    }, []);

    useEffect(() => {
        isOpenRef.current = isOpen;
        if (isOpen) updatePosition();
    }, [isOpen]);

    useEffect(() => {
        const handleCloseAll = () => setIsOpen(false);
        document.addEventListener("searchbarpagination:close-all", handleCloseAll);
        return () =>
            document.removeEventListener("searchbarpagination:close-all", handleCloseAll);
    }, []);

    useEffect(() => {
        const handlePointerDownOutside = (event) => {
            if (!isOpenRef.current) return;

            const target = event.target;
            if (wrapperRef.current?.contains(target)) return;
            if (dropdownRef.current?.contains(target)) return;

            setIsOpen(false);
        };

        const handleEscape = (event) => {
            if (event.key === "Escape" && isOpenRef.current) {
                setIsOpen(false);
            }
        };

        // Capture phase: runs before parent stopPropagation (e.g. filter drawer)
        document.addEventListener("mousedown", handlePointerDownOutside, true);
        document.addEventListener("touchstart", handlePointerDownOutside, true);
        document.addEventListener("keydown", handleEscape);

        return () => {
            document.removeEventListener("mousedown", handlePointerDownOutside, true);
            document.removeEventListener("touchstart", handlePointerDownOutside, true);
            document.removeEventListener("keydown", handleEscape);
        };
    }, []);

    const fetchData = async (prefix, pageNo = 1) => {
        const q = (prefix || "").trim();
        if (!q) return;
        if (pageNo === 1) {
            paginationPrefixRef.current = q;
        }
        setLoading(true);
        try {
            const companyQuery =
                searchAPI?.includes("GetSuggestionFromEntityType") && MasterCompanyDetails?.CompanyId
                    ? `&${paramCompanyId}=${MasterCompanyDetails.CompanyId}`
                    : "";
            const URL = `${searchAPI}${q}&page=${pageNo}${companyQuery}`;
            const sdata = await dispatch(getSearchAPI(URL));
            const result = sdata.map((obj) => Object.values(obj));

            const lastItem = result?.at(-1);
            const lastItemPre = searchResult?.at(-1);
            const isSame = JSON.stringify(lastItem) === JSON.stringify(lastItemPre);
            if (pageNo !== 1 && isSame) {
                setHasMore(false);
                return;
            } else {
                setSearchResult((prev) =>
                    pageNo === 1 ? result : [...prev, ...result],
                );
                setSearchResultObj((prev) =>
                    pageNo === 1 ? sdata : [...prev, ...sdata],
                );
                setHasMore(true);
            }

            // console.log('Result data:', searchResultObj)

        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const fetchDebounced = useDebounced((raw) => {
        const q = (raw || "").trim();
        setPage(1);
        if (q.length < minChars) {
            setSearchResult([]);
            setSearchResultObj([]);
            setHasMore(true);
            paginationPrefixRef.current = "";
            return;
        }
        fetchData(q, 1);
    }, 300);

    const handleChange = (value) => {
        setInput(value);
        onchange(value);
        fetchDebounced(value);
    };

    const handleScroll = (e) => {
        const { scrollTop, scrollHeight, clientHeight } = e.target;
        const q = (paginationPrefixRef.current || "").trim();
        if (!q) return;

        if (scrollTop + clientHeight >= scrollHeight - 10 && !loading && hasMore) {
            const nextPage = page + 1;
            setPage(nextPage);
            fetchData(q, nextPage);
        }
    };

    const trimmedInput = (input || "").trim();
    const canSearch = trimmedInput.length >= minChars;

    const handleFocus = () => {
        // Close any other open SearchBarPagination dropdowns
        document.dispatchEvent(new CustomEvent("searchbarpagination:close-all"));
        setIsOpen(true);
        const typed = trimmedInput;
        setPage(1);
        const fallback = (defaultSearch || "").trim();
        if (typed.length >= minChars) {
            fetchData(typed, 1);
        } else if (fallback) {
            fetchData(fallback, 1);
        } else {
            paginationPrefixRef.current = "";
            setSearchResult([]);
            setSearchResultObj([]);
            setHasMore(true);
        }
    };

    const dropdown = isOpen && dropdownPos ? (
        <div
            ref={dropdownRef}
            data-searchbarpagination-dropdown=""
            onMouseDown={(e) => e.stopPropagation()}
            style={{
                position: "absolute",
                top: dropdownPos.top + 2,
                left: dropdownPos.left,
                width: dropdownPos.width,
                zIndex: dropdownZIndex,
            }}
            className="bg-white border border-gray-300 dark:bg-slate-800 dark:border-slate-700 rounded-md p-1 pr-0 shadow-lg overflow-hidden"
        >
            <div onScroll={handleScroll} className="max-h-[150px] overflow-y-auto rounded-lg">
                {(searchResult || []).map((result, id) => (
                    <div
                        key={id}
                        className="cursor-pointer py-1 px-2 text-[13px] hover:bg-slate-100 dark:hover:bg-slate-700 border-b border-gray-100 dark:border-slate-700"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                            selectReacord(result);
                            const label = result?.[showResults] ?? "";
                            setInput(label);
                            setInputSelect(label);
                            setIsOpen(false);
                        }}
                    >
                        {renderItem
                            ? renderItem(searchResultObj?.[id])
                            : result[showResults]}
                    </div>
                ))}

                {loading ? (
                    <div className="p-2 flex flex-col items-center gap-2 text-sm text-gray-600">
                        <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
                    </div>
                ) : !searchResult?.length ? (
                    <div className="p-2 flex flex-col items-center justify-center text-center">
                        <SearchX className="w-6 h-6 text-gray-400 dark:text-gray-500 " />
                        <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                            {!canSearch
                                ? (defaultSearch || "").trim()
                                    ? "No sample results — try typing to search"
                                    : minChars <= 1
                                        ? "Type to search"
                                        : `Type at least ${minChars} characters`
                                : "No results found"}
                        </p>
                        {!canSearch ? null : (
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                Try a different keyword
                            </p>
                        )}
                    </div>
                ) : null}
            </div>
        </div>
    ) : null;

    return (
        <>
            <div className="relative" ref={wrapperRef}>
                <input
                    type="search"
                    placeholder={placeholder}
                    value={input}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => handleChange(e.target.value)}
                    onBlur={() => setInput(inputSelect)}
                    onFocus={handleFocus}
                    className={
                        (className ||
                            "w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white focus:ring-1 focus:ring-[#0072FF] focus:border-[#0072FF] focus:outline-none") +
                        inputStyle
                    }
                />
            </div>
            {dropdown && createPortal(dropdown, document.body)}
        </>
    );
};